<template>
  <div class="quick-estimate">
    <h2>簡易見積り</h2>
    <table class="quick-estimate-table">
      <tr>
        <td>選択したプラン</td>
        <td>
          ({{orderTypeJa[order.order_type]}}/{{mealTypeJa[order.meal_type]}}) {{order.plan_name}}
          {{order.plan_price | currency}} x {{order.quantity}}人 = {{order.quantity * order.plan_price | currency}}
        </td>
      </tr>
      <tr v-if="order.drink_sets.length > 0">
        <td>ドリンクセット</td>
        <td>
          <ul>
            <li v-for="drink_set in order.drink_sets">
              {{drink_set.name}}
              {{drink_set.price | currency}} x {{drink_set.quantity}}セット = {{drink_set.quantity * drink_set.price | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <tr v-if="singleDrinks.length > 0">
        <td>ドリンク(単品)</td>
        <td>
          <ul>
            <li v-for="drink in singleDrinks">
              {{drink.name}}
              {{drink.price | currency}} x {{drink.quantity}}{{drink.unit_name}} = {{drink.quantity * drink.price | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <tr v-if="equipments.length > 0">
        <td>備品</td>
        <td>
          <ul>
            <li v-for="equipment in equipments">
              {{equipment.name}}
              {{equipment.price | currency}} x {{equipment.quantity}}{{equipment.unit_name}} = {{equipment.quantity * equipment.price | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <tr v-if="calculationResult.drink_plans.length > 0">
        <td>ドリンクプラン</td>
        <td>
          <ul>
            <li v-for="drinkPlan in calculationResult.drink_plans">
              {{drinkPlan.name}}
              {{drinkPlan.price | currency}} x {{drinkPlan.quantity}}{{drinkPlan.unit_name}} =
              {{drinkPlan.quantity * drinkPlan.price | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <!-- 飲み放題つきのときだけ、1人あたりの目安(税抜)。「飲み放題付きで1人○円」が一目で分かるように(2026-10-12) -->
      <tr v-if="perPersonWithDrinkPlan" class="per-person-row">
        <td>1人あたりの目安</td>
        <td>
          お料理＋飲み放題で <strong>{{perPersonWithDrinkPlan | currency}}</strong>／人（税抜・送料など別）
        </td>
      </tr>
      <tr v-if="paidDrinkPlanCustomizations.length > 0">
        <td>ドリンクオプション</td>
        <td>
          <ul>
            <li v-for="customization in paidDrinkPlanCustomizations">
              {{customization.drink_plan_name}}（{{customization.description}}）
              {{customization.price | currency}} x {{customization.quantity}}人 = {{customization.sub_total | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <tr v-if="calculationResult.dishes.length > 0">
        <td>追加料理</td>
        <td>
          <ul>
            <li v-for="dish in calculationResult.dishes">
              {{dish.name}}
              {{dish.price | currency}} x {{dish.quantity}}{{dish.unit_name}} = {{dish.quantity * dish.price | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <tr v-if="paidCustomizations.length > 0">
        <td>料理内容変更</td>
        <td>
          <ul>
            <li v-for="customization in paidCustomizations">
              {{customization.description}}
              {{customization.price | currency}} x {{customization.quantity}}人 = {{customization.sub_total | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <tr v-if="order.use_tablecloth || order.use_exothermic_agent || order.use_plate">
        <td>オプション</td>
        <td>
          <ul>
            <li class="lo-benefit" v-if="order.use_plate && largeOrder.plate_free">
              100名以上特典：取皿セット 0円（通常 {{largeOrder.plate_regular_price | currency}}）
            </li>
            <li v-else-if="order.use_plate">
              取皿セット: {{calculationResult.plate.price | currency}}
            </li>
            <li class="lo-benefit" v-if="order.use_tablecloth && largeOrder.tablecloth_free">
              100名以上特典：テーブルクロス 0円（通常 {{largeOrder.tablecloth_regular_price | currency}}）
            </li>
            <li v-else-if="order.use_tablecloth">
              テーブルクロス: {{calculationResult.tablecloth.price | currency}}
            </li>
            <li v-if="order.use_exothermic_agent">
              発熱剤: {{calculationResult.exothermic_agent.price | currency}}
            </li>
          </ul>
        </td>
      </tr>
      <tr v-if="order.order_type === 'catering'">
        <td>スタッフ派遣料</td>
        <td>
          <ul>
            <li class="lo-benefit" v-if="largeOrder.staff_free">
              100名以上特典：{{largeOrder.staff_label}} 0円（通常 {{largeOrder.staff_regular_price | currency}}）
            </li>
            <li v-else>
              {{calculationResult.worker.price | currency}}
              <span v-if="calculationResult.drink_plans.length > 0">
                (バーテンダー料金)
              </span>
              <span v-else>
                (設置・撤収料金)
              </span>
            </li>
          </ul>
        </td>
      </tr>
      <!-- 100名以上特典: お寿司を増量(無料) -->
      <tr v-if="largeOrder.sushi_more">
        <td>100名以上特典</td>
        <td><span class="lo-benefit">お寿司を増量（無料）</span></td>
      </tr>
      <!-- 100名以上特典: 早期ご予約割(注文日から催事日まで30日以上で料理代3%引き) -->
      <tr v-if="largeOrder.early_discount > 0">
        <td>{{largeOrder.early_discount_label}}</td>
        <td><span class="lo-benefit">−{{largeOrder.early_discount | currency}}</span></td>
      </tr>
      <tr>
        <td>送料</td>
        <td>
          {{calculationResult.delivery_price | currency}}
          <!-- 料理の金額の条件で送料が無料になったとき、本来の送料を併記(100名以上特典ではないので色は付けない) -->
          <span v-if="calculationResult.delivery_price === 0 && calculationResult.delivery_regular_price > 0">（通常 {{calculationResult.delivery_regular_price | currency}}）（送料無料の条件を満たしています）</span>
          <span v-else-if="order.city">({{order.prefecture}}{{order.city}}への送料)</span>
        </td>
      </tr>

      <tr>
        <td>小計金額</td>
        <td>{{calculationResult.normal_tax_rate.sub_total + calculationResult.reduced_tax_rate.sub_total | currency}}</td>
      </tr>

      <tr>
        <td>消費税</td>
        <td>{{calculationResult.normal_tax_rate.tax + calculationResult.reduced_tax_rate.tax | currency}}</td>
      </tr>

      <tr>
        <td>合計金額</td>
        <td>{{calculationResult.total_price | currency}}</td>
      </tr>
    </table>

    <table class="mt-2 tax-table">
      <tr class="reduced-tax-rate" v-if="order.order_type === 'delivery'">
        <td>8%対象</td>
        <td>{{calculationResult.reduced_tax_rate.sub_total | currency }}</td>
        <td>消費税</td>
        <td>{{calculationResult.reduced_tax_rate.tax | currency }}</td>
      </tr>
      <tr class="normal-tax-rate">
        <td>10%対象</td>
        <td>{{calculationResult.normal_tax_rate.sub_total | currency }}</td>
        <td>消費税</td>
        <td>{{calculationResult.normal_tax_rate.tax | currency }}</td>
      </tr>
    </table>
  </div>
</template>

<script>
  import { orderTypeJa, mealTypeJa }  from '../../utilities/locale';
  export default {
    props: {
      order: Object,
      calculationResult: Object
    },
    data: function () {
      return {
        orderTypeJa,
        mealTypeJa
      }
    },
    methods: {
    },
    updated() {
    },
    computed: {
      // お料理の単価 ＋ 飲み放題(人数分の合計 ÷ お料理の人数)。飲み放題が無い・人数が無いときは null
      perPersonWithDrinkPlan() {
        const quantity = Number(this.order.quantity) || 0
        const drinkPlans = (this.calculationResult.drink_plans || []).filter(d => Number(d.quantity) > 0)
        if (quantity <= 0 || drinkPlans.length === 0) return null
        const drinkTotal = drinkPlans.reduce((sum, d) => sum + Number(d.price) * Number(d.quantity), 0)
        return Math.round(Number(this.order.plan_price) + drinkTotal / quantity)
      },
      singleDrinks() {
        return this.order.drinks.filter(option => option.kind !== 'other');
      },
      equipments() {
        return this.order.drinks.filter(option => option.kind === 'other');
      },
      paidCustomizations() {
        return (this.calculationResult.customizations || []).filter(c => Number(c.price) > 0);
      },
      paidDrinkPlanCustomizations() {
        return (this.calculationResult.drink_plan_customizations || []).filter(c => Number(c.price) > 0);
      },
      // 100名以上の大口特典。古い計算結果(キー無し)でも表示が壊れないようにする
      largeOrder() {
        return this.calculationResult.large_order || {};
      },
    }
  } </script>

<style>
  /* 100名以上特典を目立たせる(注文完了ページの注意書きと同じ色)。右の欄の中だけに帯で色を付ける。
     クラス名は lo- で始めて他と衝突させない */
  .lo-benefit {
    display: block;
    list-style: none;
    padding: 2px 8px;
    border-radius: 4px;
    background: #F8DECC;
    color: #A63508;
    font-weight: 700;
  }
</style>
